import React, { FunctionComponent } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import FastImage from 'react-native-fast-image';
import Icon from 'react-native-vector-icons/dist/FontAwesome';
import { connect } from 'react-redux';
import Theme from '../../../theme';
import { defaultScale } from '../../../utils/scaling';
import { pausePlayback, selectTrack } from './actions';
import { IPlayerState, ISong } from './interfaces';
import { getMusicPlayerStateSelector, getPremiumUserSelector, getSelectedMusicPlayerSelector } from './selectors';
import { orientationSelector } from '../../selectors';
import { musicPlatform } from 'fitbeat.gym-core/config';

export interface IPlaylistItemProps {
  playlistItem: ISong;
  premiumUser: boolean;
  playerState: IPlayerState | null;
  selectTrack: (track: ISong) => void;
  pausePlayback: () => void;
  index: number;
  orientation: string;
  musicPlayer: musicPlatform;
}

export const truncate = (str: string, chars: number): string => {
  if (!str || str.length < chars) {
    return str;
  }
  return `${str.substring(0, chars - 3)}...`;
};

const millisToMinutesAndSeconds = (millis) => {
  const minutes = Math.floor(millis / 60000);
  const seconds = ((millis % 60000) / 1000).toFixed(0);
  return `${minutes}:${(seconds.length < 2 ? '0' : '')}${seconds}`;
};

const SongListItemComponent: FunctionComponent<IPlaylistItemProps> = ({ playlistItem, ...props }) => {

  const showBorderTop = () => {
    return props.orientation === 'PORTRAIT' && props.index === 0;
  };

  const playlistItemIcon = (): Icon | void => {
    if (!props.premiumUser || props.musicPlayer === musicPlatform.AppleMusic) {
      return;
    }
    if (playlistItem.isPlaying) {
      return <Icon name='pause' size={defaultScale(20)} color={Theme.WHITE}/>;
    }
    return <Icon name='play' size={defaultScale(20)} color={Theme.WHITE}/>;
  };

  return (
    <TouchableOpacity style={[styles.playlistItem, showBorderTop() && styles.borderTop]}
                      disabled={!props.premiumUser || props.musicPlayer === musicPlatform.AppleMusic}
                      onPress={() => {
                        if (playlistItem.isPlaying) {
                          return props.pausePlayback();
                        } else if (props.premiumUser) {
                          props.selectTrack(playlistItem);
                        }
                      }}>
      <View style={styles.playlistItemContent}>
        <View style={styles.albumCoverContainer}>
          <FastImage
            style={{ width: styles.albumCoverContainer.width, height: styles.albumCoverContainer.height }}
            source={{
              uri: playlistItem.thumbnail,
            }}
            resizeMode={FastImage.resizeMode.contain}
          />
        </View>
        <View style={{ paddingLeft: defaultScale(15), flexGrow: 1 }}>
          <Text style={styles.playlistItemTitle}>{truncate(playlistItem.title, 37)}</Text>
          <View style={{ flexDirection: 'row' }}>
            <Text style={[styles.playlistItemSubtitle, { flex: 2 }]}>{truncate(playlistItem.artist, 25)}</Text>
            <Text style={[styles.playlistItemSubtitle, { flex: 1 }]}>
              {millisToMinutesAndSeconds(playlistItem.duration)}</Text>
          </View>
        </View>
        <View style={{ justifyContent: 'center' }}>
          {playlistItemIcon()}
        </View>
      </View>
    </TouchableOpacity>
  );
};

const mapStateToProps = (state) => {
  return {
    premiumUser: getPremiumUserSelector(state),
    playerState: getMusicPlayerStateSelector(state),
    orientation: orientationSelector(state),
    musicPlayer: getSelectedMusicPlayerSelector(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    selectTrack: (song) => dispatch(selectTrack(song)),
    pausePlayback: () => dispatch(pausePlayback()),
  };
};

export const SongListItem = connect(mapStateToProps, mapDispatchToProps)(SongListItemComponent);

const styles = StyleSheet.create({
  playlistItem: {
    borderBottomWidth: 1,
    borderBottomColor: Theme.GREY_DARK,
    marginLeft: defaultScale(5),
  },
  borderTop: {
    borderTopWidth: 1,
    borderTopColor: Theme.GREY_DARK,
  },
  playlistItemContent: {
    flexDirection: 'row',
    paddingVertical: defaultScale(14),
    paddingHorizontal: defaultScale(8),
  },
  playlistItemTitle: {
    ...Theme.textStyle.bodyCopyBold,
    color: Theme.WHITE,
    paddingBottom: defaultScale(5),
  },
  playlistItemSubtitle: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.GREY,
  },
  albumCoverContainer: {
    height: defaultScale(45),
    width: defaultScale(45),
    backgroundColor: Theme.CHARCOAL,
  },
});
